import {normalizeMessarioOpenPeer} from '@lib/messarioLaunch/coordinatorCore';

export const MESSARIO_OPEN_PEER_KEY = 'messario_launch_open_peer';
export const MESSARIO_OPEN_PEER_EVENT = 'messario_open_peer';

export function saveMessarioOpenPeer(
  peer: string,
  storage: Storage = window.sessionStorage
) {
  const normalized = normalizeMessarioOpenPeer(peer);
  if(!normalized) {
    return;
  }

  storage.setItem(MESSARIO_OPEN_PEER_KEY, normalized);
  try {
    window.dispatchEvent(new CustomEvent(MESSARIO_OPEN_PEER_EVENT, {detail: normalized}));
  } catch(err) {
    // window may be unavailable in unit tests
  }
}

export function peekMessarioOpenPeer(
  storage: Storage = window.sessionStorage
): string | undefined {
  return normalizeMessarioOpenPeer(storage.getItem(MESSARIO_OPEN_PEER_KEY) || undefined);
}

export function consumeMessarioOpenPeer(
  storage: Storage = window.sessionStorage
): string | undefined {
  const peer = peekMessarioOpenPeer(storage);
  storage.removeItem(MESSARIO_OPEN_PEER_KEY);
  return peer;
}

/** Listen for the whole document lifetime; retry only while an actual intent needs opening. */
export function installMessarioOpenPeerHandler(options: {
  openPeer: (peer: string) => Promise<boolean>,
  onError: (error: unknown) => void,
  storage?: Storage,
  target?: Window
}): () => void {
  const storage = options.storage ?? window.sessionStorage;
  const target = options.target ?? window;
  const retryGaps = [300, 500, 700, 1500, 2000, 3000];
  let timer: ReturnType<typeof setTimeout>;
  let revision = 0;
  let attempt = 0;
  let running = false;
  let disposed = false;

  const tryOpen = async() => {
    if(running || disposed) return;
    running = true;
    try {
      while(!disposed) {
        const peer = peekMessarioOpenPeer(storage);
        if(!peer) break;
        const currentRevision = revision;
        let opened = false;
        try {
          opened = await options.openPeer(peer);
        } catch(error) {
          options.onError(error);
        }

        if(disposed) break;
        // A click during the await supersedes this attempt, including its consumption.
        if(currentRevision !== revision) continue;
        if(opened && peekMessarioOpenPeer(storage) === peer) {
          consumeMessarioOpenPeer(storage);
        } else if(attempt < retryGaps.length) {
          timer = setTimeout(() => void tryOpen(), retryGaps[attempt++]);
        }
        break;
      }
    } finally {
      running = false;
    }
  };

  const onEvent = () => {
    revision++;
    attempt = 0;
    clearTimeout(timer);
    void tryOpen();
  };
  target.addEventListener(MESSARIO_OPEN_PEER_EVENT, onEvent);
  void tryOpen();

  return () => {
    disposed = true;
    clearTimeout(timer);
    target.removeEventListener(MESSARIO_OPEN_PEER_EVENT, onEvent);
  };
}

export function messarioImHashForPeer(peer: string): string {
  return `#/im?p=${peer}`;
}
